import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ViewListIcon from '@material-ui/icons/ViewList';
import CircleIcon from '@material-ui/icons/FiberManualRecord';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import MuiSwitch from '@material-ui/core/Switch';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';

import LanguageSelect from '../LanguageSelect';
import { GetHelp, InfoButton } from '../FormWidgets/Description';

import { statusColors } from '../Constants/constants';
import { useAuth } from '../Hooks/authHook';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';
import { isZoned } from '../Utils/utils';

const useStyles = makeStyles((theme) => ({
  list: {
    width: 250
  },
  sliderLockItem: {
    alignItems: 'stretch',
    flexDirection: 'column'
  },
  sliderLockLabel: {
    marginBottom: theme.spacing(1)
  },
  sliderLockButtons: {
    width: '100%'
  },
  sliderLockButton: {
    flex: 1,
    minWidth: 0,
    '& .MuiButton-label': {
      overflowWrap: 'normal',
      whiteSpace: 'nowrap'
    }
  },
  editModeControl: {
    marginRight: theme.spacing(0.5)
  }
}));

// Disabled until GUI zoom no longer breaks pointer alignment on sliders/touch controls.
const SHOW_MENU_ZOOM_CONTROLS = false;

export default function MainMenu({
  open,
  onClose,
  toggleTheme,
  currTheme,
  toggleUserMenu,
  onNavigate,
  setLang,
  lockSliderSetting,
  setLockSliderSetting
}) {
  const classes = useStyles();
  const auth = useAuth();
  let { t } = useTranslation();
  const editModeHelp = GetHelp('edit_mode');

  const stateManager = getStateManager();
  const controllers = useSnapshot(stateManager.controllers);

  const zoom = function (amount) {
    let new_zoom = Number(localStorage.getItem('zoom') ?? 1) + amount;
    document.body.style.zoom = new_zoom;
    localStorage.setItem('zoom', new_zoom);
  };

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <div className={classes.list} role="presentation">
        <List>
          <ListItem></ListItem>

          <ListItem>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexWrap: 'nowrap',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <IconButton
                  edge="end"
                  onClick={toggleUserMenu(true)}
                  aria-label="account of current user"
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
                <LanguageSelect setLang={setLang} onSelect={onNavigate} />
              </div>

              {SHOW_MENU_ZOOM_CONTROLS && (
                <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <IconButton
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                      zoom(0.2);
                    }}
                  >
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                      zoom(-0.2);
                    }}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                </div>
              )}
            </div>
          </ListItem>
          <ListItem>
            <FormControlLabel
              className={classes.editModeControl}
              control={
                <MuiSwitch
                  checked={currTheme === 'light' ? false : true}
                  onClick={toggleTheme}
                  color="primary"
                />
              }
              label="Darkmode"
            />
          </ListItem>
          <ListItem></ListItem>
          <ListItem>
            <FormControlLabel
              control={
                <MuiSwitch
                  checked={stateManager.editMode}
                  onClick={() => (stateManager.editMode = !stateManager.editMode)}
                  color="primary"
                />
              }
              label={t('edit_mode')}
            />
            <InfoButton
              brief={editModeHelp.brief}
              content={editModeHelp.content}
              img={editModeHelp.img}
              table={editModeHelp.table}
            />
          </ListItem>
          <ListItem className={classes.sliderLockItem}>
            <span className={classes.sliderLockLabel}>{t('slider_lock')}</span>
            <ButtonGroup
              className={classes.sliderLockButtons}
              color="primary"
              aria-label={t('slider_lock')}
            >
              {['auto', 'on', 'off'].map((mode) => (
                <Button
                  className={classes.sliderLockButton}
                  key={mode}
                  onClick={() => setLockSliderSetting(mode)}
                  variant={lockSliderSetting === mode ? 'contained' : 'outlined'}
                  aria-pressed={lockSliderSetting === mode}
                >
                  {t(`slider_lock_${mode}`)}
                </Button>
              ))}
            </ButtonGroup>
          </ListItem>
        </List>
        {auth.user && (
          <React.Fragment>
            <Divider />
            <List>
              <ListItem
                button
                key="Controller Overview"
                component={Link}
                to="/"
                onClick={onNavigate}
              >
                <ListItemIcon>
                  <ViewListIcon />
                </ListItemIcon>
                <ListItemText primary={t('manage_controllers')} />
              </ListItem>
            </List>
            <Divider />
            <List>
              {controllers.map((controller) => (
                <ListItem
                  button
                  key={controller.name}
                  component={Link}
                  onClick={onNavigate}
                  to={
                    `/controller/${controller.id}` +
                    (isZoned(controller) ? '/zones/zone_0/info' : '/info')
                  }
                >
                  <ListItemIcon>
                    <CircleIcon
                      style={{ color: statusColors[controller?.connection?.status ?? 'bad'] }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={controller.name} />
                </ListItem>
              ))}
            </List>
          </React.Fragment>
        )}
      </div>
    </Drawer>
  );
}
